/**
 * Sprint E — les 5 étapes métier du module Soumissions unifié
 * (À FAIRE / PROPOSÉE / À SUIVRE / GAGNÉE / PERDUE).
 *
 * Cette règle est un miroir intentionnel de la fonction SQL
 * `commercial_submission_stage_v1` (voir
 * `supabase/migrations/20260819020000_commercial_submissions_feed_v1.sql`),
 * pas une source alternative : la vue `commercial_submissions_feed` calcule
 * l'étape côté serveur pour le WHERE/ORDER PAR de la liste (performance,
 * section 57 de la tâche); cette fonction sert l'affichage client (badges,
 * regroupement d'onglets côté React) et ses tests unitaires. Les deux DOIVENT
 * encoder exactement la même règle — toute modification de l'une doit être
 * répercutée dans l'autre au même commit.
 */

export type SubmissionStage = 'A_FAIRE' | 'PROPOSEE' | 'A_SUIVRE' | 'PAIEMENT' | 'GAGNEE' | 'PERDUE'

export const submissionStageLabel: Record<SubmissionStage, string> = {
  A_FAIRE: 'À faire',
  PROPOSEE: 'Proposée',
  A_SUIVRE: 'À suivre',
  PAIEMENT: 'Paiement',
  GAGNEE: 'Gagnée',
  PERDUE: 'Perdue',
}

export type SubmissionLeadStatus =
  | 'NEW'
  | 'TO_CONTACT'
  | 'CALLBACK'
  | 'NO_ANSWER'
  | 'CONTACTED_NOT_INTERESTED'
  | 'INTERESTED'
  | 'CONVERTED'
  | 'ARCHIVED'

export type SubmissionContractStatus =
  | 'DRAFT'
  | 'PENDING_CLIENT'
  | 'VIEWED'
  | 'SIGNED'
  | 'AWAITING_PAYMENT'
  | 'ACTIVE'
  | 'DECLINED'
  | 'EXPIRED'
  | 'CANCELLED'
  | 'COMPLETED'
  | 'SUSPENDED'

export const DEFAULT_FOLLOW_UP_DELAY_HOURS = 48

/**
 * Calcule l'étape d'une ligne du flux Soumissions. Exactement un des deux
 * paramètres `leadStatusV4`/`contractStatusV2` doit être fourni — comme la
 * vue SQL, une ligne vient soit d'un Lead soit d'un Contrat, jamais des deux.
 */
export function commercialSubmissionStage(input: {
  leadStatusV4?: SubmissionLeadStatus
  contractStatusV2?: SubmissionContractStatus
  sentAt?: string | Date | null
  followUpDelayHours?: number
  now?: Date
}): SubmissionStage | null {
  const { leadStatusV4, contractStatusV2 } = input
  const delayHours = input.followUpDelayHours ?? DEFAULT_FOLLOW_UP_DELAY_HOURS
  const now = input.now ?? new Date()

  if (contractStatusV2) {
    switch (contractStatusV2) {
      case 'DRAFT':
        return 'A_FAIRE'
      case 'PENDING_CLIENT':
      case 'VIEWED':
        return isWithinFollowUpDelay(input.sentAt, delayHours, now) ? 'PROPOSEE' : 'A_SUIVRE'
      // Révision du 17 sept 2026 (propriétaire) : signé n'est plus « gagné ».
      // Le Client a accepté, mais le Contrat n'entre en service qu'au
      // paiement — la Demande reste en PAIEMENT jusqu'à ACTIVE.
      case 'SIGNED':
      case 'AWAITING_PAYMENT':
        return 'PAIEMENT'
      // SUSPENDED est une décision de jugement (non couverte explicitement
      // par la tâche, documentée aussi côté SQL) : un Contrat suspendu a été
      // gagné puis mis en pause, il ne redevient pas une soumission en cours.
      case 'ACTIVE':
      case 'SUSPENDED':
        return 'GAGNEE'
      // EXPIRED : décision de jugement, offre caduque = fonctionnellement perdue.
      case 'DECLINED':
      case 'CANCELLED':
      case 'EXPIRED':
        return 'PERDUE'
      // COMPLETED ne devrait jamais apparaître dans le flux (exclu par la
      // vue) : aucune étape des 5 ne correspond, saison terminée.
      case 'COMPLETED':
        return null
      default:
        return null
    }
  }

  if (leadStatusV4) {
    switch (leadStatusV4) {
      case 'CONTACTED_NOT_INTERESTED':
        return 'PERDUE'
      case 'CONVERTED':
      case 'ARCHIVED':
        // Exclu du flux par la vue (Lead converti/archivé) : pas d'étape.
        return null
      // NEW/TO_CONTACT/CALLBACK/NO_ANSWER/INTERESTED restent tous À FAIRE
      // (section 11 : l'absence de réponse ne fait jamais basculer en PERDUE).
      case 'NEW':
      case 'TO_CONTACT':
      case 'CALLBACK':
      case 'NO_ANSWER':
      case 'INTERESTED':
        return 'A_FAIRE'
    }
  }

  return null
}

/**
 * Refonte Demandes, section 12 : chaque Demande doit afficher une prochaine
 * action explicite plutôt qu'une simple date. Dérivé uniquement de champs
 * déjà présents sur `SubmissionRecord` — aucune donnée inventée.
 */
export function nextActionLabelForSubmission(input: {
  stage: SubmissionStage
  nextFollowUpAt?: string | undefined
  lastFollowUpAt?: string | undefined
  now?: Date | undefined
}): string {
  const { stage, nextFollowUpAt, lastFollowUpAt } = input
  const now = input.now ?? new Date()

  if (stage === 'PAIEMENT' || stage === 'GAGNEE' || stage === 'PERDUE') return 'Aucune action'

  if (nextFollowUpAt) {
    const due = new Date(nextFollowUpAt)
    const dueDay = new Date(due.getFullYear(), due.getMonth(), due.getDate())
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const dayDiff = Math.round((dueDay.getTime() - today.getTime()) / 86_400_000)
    const time = new Intl.DateTimeFormat('fr-CA', { hour: '2-digit', minute: '2-digit' }).format(due)
    if (dayDiff < 0) return 'Appeler maintenant'
    if (dayDiff === 0) return `Rappeler aujourd’hui ${time}`
    if (dayDiff === 1) return `Rappeler demain ${time}`
    return `Rappeler le ${new Intl.DateTimeFormat('fr-CA', { month: 'short', day: 'numeric' }).format(due)}`
  }

  if (stage === 'A_FAIRE' && !lastFollowUpAt) return 'Appeler maintenant'
  if (stage === 'PROPOSEE' || stage === 'A_SUIVRE') return 'Relancer la proposition'
  return 'Appeler maintenant'
}

/**
 * Refonte Demandes, section 16 : priorité (1) rappels en retard, (2) reçues
 * récemment sans contact, (3) rappels du jour, (4) plus anciennes sans
 * action. Tri d'affichage côté client uniquement, comme
 * `sortByFollowUpUrgency` qu'elle remplace — ne change rien à
 * `commercialSubmissionStage`.
 */
export function submissionPriorityTier(input: {
  nextFollowUpAt?: string | undefined
  lastFollowUpAt?: string | undefined
  now?: Date | undefined
}): 1 | 2 | 3 | 4 {
  const now = input.now ?? new Date()
  if (input.nextFollowUpAt) {
    const due = new Date(input.nextFollowUpAt)
    if (due.getTime() < now.getTime()) return 1
    const dueDay = new Date(due.getFullYear(), due.getMonth(), due.getDate())
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    if (dueDay.getTime() === today.getTime()) return 3
  }
  if (!input.lastFollowUpAt) return 2
  return 4
}

export function isWithinFollowUpDelay(
  sentAt: string | Date | null | undefined,
  delayHours: number,
  now: Date,
): boolean {
  if (!sentAt) return false
  const sentAtDate = typeof sentAt === 'string' ? new Date(sentAt) : sentAt
  const elapsedHours = (now.getTime() - sentAtDate.getTime()) / (1000 * 60 * 60)
  return elapsedHours <= delayHours
}
